import { Component, OnInit, OnDestroy, ViewChild, AfterViewInit } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, FormControl, Validators } from '@angular/forms';
import { MatTableDataSource } from '@angular/material/table';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { TablerIconsModule } from 'angular-tabler-icons';
import { MaterialModule } from 'src/app/material.module';
import { ReportsService } from '../reports.service';
import { IPatientReport, IReportRequest, IPagedPatientReportResult } from '../ireports';
import { Subject, takeUntil, Observable, startWith, map, debounceTime } from 'rxjs';
import { CommonDropDown } from 'src/app/common/types/dropdown.type';
import { LookupService } from 'src/app/services/lookup.service';
import { LoginService } from 'src/app/login.service';
import { ToastrService } from 'ngx-toastr';
import { DATE_FORMAT } from 'src/app/common/constant/constants.type';
import { BaseRespone, PaginationResult } from 'src/app/models/common.response';
import { HttpClient } from '@angular/common/http';
import { IHospitalInfo } from '../../hospital/ihospital-info';
import { HospitalStateService } from 'src/app/services/hospitalstate.service';
import { environment } from 'src/environments/environment';
import { PAGE_SIZE_OPTIONS } from 'src/app/common/constant/constants.type';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MomentDateAdapter, MAT_MOMENT_DATE_ADAPTER_OPTIONS } from '@angular/material-moment-adapter';
import { MY_FORMATS } from 'src/app/common/dateFormat';
import { MatPaginator } from '@angular/material/paginator';
import { Router } from '@angular/router';
import { ReportColumn, ReportPrintService } from '../../../services/report-print.service';
import { DefaultValuePipe } from 'src/app/pipe/default-value.pipe';

@Component({
  selector: 'app-report-list',
  imports: [
    CommonModule,
    MaterialModule,
    TablerIconsModule,
    FormsModule,
    ReactiveFormsModule,
    TranslateModule,
    DefaultValuePipe
  ],
  providers: [
    DatePipe,
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS]
    },
    { provide: MAT_DATE_FORMATS, useValue: MY_FORMATS },
  ],
  templateUrl: './report-list.component.html',
  styleUrl: './report-list.component.scss'
})
export class ReportListComponent implements OnInit, OnDestroy, AfterViewInit {
  private destroy$ = new Subject<void>();
  ReportForm: FormGroup;
  doctorControls = new FormControl('');
  doctorslist: CommonDropDown[] = [];
  selectedDoctors: CommonDropDown[] = [];
  isDoctor = false;
  pageSizeOptions = PAGE_SIZE_OPTIONS;
  loggedInDoctorId: number | null = null;
  filteredDoctorList$: Observable<CommonDropDown[]>;
  showAddOptionforDoctorList = false;
  minDate: Date;
  maxDate: Date = new Date();

  searchText: string = '';
  displayedColumns = ['opdDate', 'opdNo', 'patientName', 'mobile', 'address', 'diagnosis'];
  dataSource = new MatTableDataSource<IPatientReport>();
  @ViewChild(MatPaginator) paginator: MatPaginator;

  isLoading: boolean = false;
  hospitalInfo: IHospitalInfo;
  staticimagepath = environment.imagePath;
  reportItems: any;
  isPrintReport: boolean = false;

  pagination: PaginationResult;
  reportCount: number = 0;
  patientSearchControl = new FormControl('');

  constructor(
    private _reportService: ReportsService,
    private _lookupService: LookupService,
    private _loginService: LoginService,
    private fb: FormBuilder,
    private datePipe: DatePipe,
    private toastr: ToastrService,
    private translate: TranslateService,
    private http: HttpClient,
    private hospitalData: HospitalStateService,
    private router: Router,
    private printService: ReportPrintService,
  ) { }

  ngOnInit(): void {
    this.isDoctor = this._loginService.isDoctor();
    this.hospitalInfo = this.hospitalData.getHospital();

    const now = new Date();
    this.ReportForm = this.fb.group({
      doctorIds: [[]],
      startDate: [new Date(now.getFullYear(), now.getMonth(), 1), Validators.required],
      endDate: [new Date(), Validators.required]
    });

    this._lookupService.getDoctors()
      .pipe(takeUntil(this.destroy$))
      .subscribe((data) => {
        this.doctorslist = data || [];

        if (this.isDoctor) {
          this.loggedInDoctorId = this._loginService.getUserId();
          this.ReportForm.patchValue({
            doctorIds: [this.loggedInDoctorId]
          });

          const doc = this.doctorslist.find(d => d.id === this.loggedInDoctorId);
          if (doc) {
            this.selectedDoctors = [doc];
            this.doctorControls.setValue(doc.name, { emitEvent: false });
          }
        } else {
          this.filteredDoctorList$ = this.doctorControls.valueChanges.pipe(
            startWith(''),
            debounceTime(300),
            map((value) => {
              const val = typeof value === 'string' ? value : '';
              const filtered = this._filterDoctorContent(val);
              this.showAddOptionforDoctorList =
                !!val && !filtered.some((x) => x.name.toLowerCase() === val.toLowerCase());
              return filtered;
            })
          );

          // Sync selected doctors with FormGroup
          this.doctorControls.valueChanges
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
              const selectedIds = this.selectedDoctors.map(d => d.id);
              this.ReportForm.get('doctorIds')?.setValue(selectedIds, { emitEvent: false });
            });
        }
      });

    this._reportService.pagination$
      .pipe(takeUntil(this.destroy$))
      .subscribe((pagination: PaginationResult | null) => {
        if (pagination) {
          this.pagination = pagination;
        }
      });

    this._reportService.opd$.pipe(takeUntil(this.destroy$)).subscribe(data => {
      if (data) {
        this.dataSource.data = data || [];
        this.reportItems = data || []; // Keep reportItems in sync for printing
        this.dataSource.paginator = this.paginator;
        this.reportCount = data?.length || 0;
      }
    });

    this.dataSource.filterPredicate = (data: IPatientReport, filter: string): boolean => {
      const search = filter?.toLowerCase() ?? '';
      const patientName = (data.patientName ?? '').toLowerCase();
      const opdNo = (data.opdNo ?? '').toString().toLowerCase();
      return patientName.includes(search) || opdNo.includes(search);
    };

    this.patientSearchControl.valueChanges
      .pipe(debounceTime(300), takeUntil(this.destroy$))
      .subscribe((value: string | null) => {
        const filterValue = (value ?? '').trim().toLowerCase();
        this.dataSource.filter = filterValue;
        if (this.dataSource.paginator) {
          this.dataSource.paginator.firstPage();
        }
      });

    // Auto-search on load
    this.OnSearch();
  }

  ngAfterViewInit() {
    this.dataSource.paginator = this.paginator;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private _filterDoctorContent(value: string): CommonDropDown[] {
    const filterValue = value.toLowerCase();
    return this.doctorslist.filter(option =>
      option.name.toLowerCase().includes(filterValue)
    );
  }

  onDoctorToggle(doc: CommonDropDown): void {
    const index = this.selectedDoctors.findIndex(d => d.id === doc.id);

    if (index > -1) {
      this.selectedDoctors.splice(index, 1);
    } else {
      this.selectedDoctors.push(doc);
    }

    this.ReportForm.get('doctorIds')?.setValue(this.selectedDoctors.map(d => d.id));

    this.doctorControls.setValue(
      this.selectedDoctors.map(d => d.name).join(', '),
      { emitEvent: false }
    );
  }

  isDoctorSelected(id: number): boolean {
    return this.selectedDoctors.some(d => d.id === id);
  }

  OnSearch(): void {
    if (this.ReportForm.invalid) {
      return;
    }

    const formValue = this.ReportForm.getRawValue();

    if (formValue.startDate && formValue.endDate) {
      const startDate = new Date(formValue.startDate);
      const endDate = new Date(formValue.endDate);

      if (endDate < startDate) {
        this.toastr.error(this.translate.instant('DATE_COMPARE_START_END'));
        return;
      }
    }

    const requestData: IReportRequest = {
      ...formValue,
      startDate: formValue.startDate && this.datePipe.transform(formValue.startDate, DATE_FORMAT),
      endDate: formValue.endDate && this.datePipe.transform(formValue.endDate, DATE_FORMAT),
      pageNumber: 0,
      pageSize: 0
    };

    this.isLoading = true;
    this._reportService.getPatientOpdReport(requestData).subscribe({
      next: (response: BaseRespone<IPagedPatientReportResult>) => {
        this.isLoading = false;
        this.isPrintReport = true;
        if (!response.isSuccess) {
          this.toastr.error(response.message || 'Error fetching report');
        }
      },
      error: () => {
        this.isLoading = false;
        this.toastr.error('Error fetching report');
      }
    });
  }

  clearFilters() {
    this.isPrintReport = false;
    const now = new Date();

    if (!this.isDoctor) {
      this.selectedDoctors = [];
      this.doctorControls.setValue('', { emitEvent: false });
      this.ReportForm.patchValue({
        doctorIds: []
      });
    }

    this.patientSearchControl.setValue('');
    this.dataSource.filter = '';

    this.ReportForm.patchValue({
      startDate: new Date(now.getFullYear(), now.getMonth(), 1),
      endDate: new Date()
    });
    this.OnSearch();
  }

  async printReport() {
    const { startDate, endDate, doctorIds } = this.ReportForm.getRawValue();
    const columns: ReportColumn[] = [
      {
        header: 'OPD Date', field: 'opdDate', flex: '2',
        formatter: (val) => this.datePipe.transform(val, 'dd-MM-yyyy') ?? ''
      },
      { header: 'OPD No', field: 'opdNo', flex: '2' },
      { header: 'Patient Name', field: 'patientName', flex: '3' },
      { header: 'Mobile', field: 'mobile', flex: '2' },
      { header: 'Address', field: 'address', flex: '2' },
      { header: 'Diagnosis', field: 'diagnosis', flex: '3' },
    ];

    const doctorArray = Array.isArray(doctorIds)
      ? doctorIds
      : (doctorIds ? [doctorIds] : []);

    if (doctorArray.length === 0) {
      columns.push({
        header: 'Doctor',
        field: 'doctorName',
        flex: '2'
      });
    }

    await this.printService.printReport({
      title: this.translate.instant('LABEL.PATIENT_REPORT_OPD'),
      items: this.reportItems,
      columns: columns,
      startDate: startDate,
      endDate: endDate,
      doctorNames: this.selectedDoctors.map(d => d.name).join(', ')
    });
  }

  viewOPD(row: IPatientReport): void {
    if (row.patientId && row.opdId) {
      this.router.navigate(['/opd', row.patientId, 'consultation', row.opdId]);
    }
  }
}